import React, { useEffect, useState } from 'react';
import {
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import customFetch from '../utils/customFetch';
import { persistLoginSession } from '../utils/persistLoginSession';
import { safeRedirectPath } from '../utils/safeRedirect';
import { toast } from 'react-toastify';
import LogoLang from '../components/LogoLang';

export const loader = async () => {
  try {
    const { data } = await customFetch.get('/auth/mfa/challenge');
    return data;
  } catch (error) {
    return redirect('/login');
  }
};

const methodLabels = {
  totp: 'Authenticator-App',
  emailOtp: 'E-Mail-Code',
  webauthn: 'Passkey',
};

const inputClasses =
  'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6';

const primaryButtonClasses =
  'flex w-full justify-center rounded-md bg-svisit-primary-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300';

const MfaVerify = () => {
  const { methods, hasRecoveryCodes, email } = useLoaderData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const preferredMethod =
    ['totp', 'webauthn', 'emailOtp'].find((m) => methods.includes(m)) ||
    methods[0];
  const [method, setMethod] = useState(preferredMethod);
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  useEffect(() => {
    if (emailCooldown <= 0) return;
    const timer = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailCooldown]);

  const finishLogin = (tokenUser) => {
    persistLoginSession(queryClient, tokenUser);
    navigate(safeRedirectPath(searchParams.get('redirectURL')));
  };

  const handleVerifyError = (error) => {
    const msg = error?.response?.data?.msg;
    toast.error(msg || 'Verifikation fehlgeschlagen');
    // Challenge abgelaufen oder gesperrt → zurück zum Passwort-Login
    if (msg?.includes('logge dich erneut') || msg?.includes('abgelaufen')) {
      navigate('/login');
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;
    setIsSubmitting(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/verify', {
        method,
        code: code.trim(),
      });
      finishLogin(data.tokenUser);
    } catch (error) {
      handleVerifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendEmailCode = async () => {
    setIsSubmitting(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/email-otp/send');
      toast.success(data.msg);
      setEmailCodeSent(true);
      setEmailCooldown(60);
    } catch (error) {
      handleVerifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyWithPasskey = async () => {
    setIsSubmitting(true);
    try {
      const { data } = await customFetch.get('/auth/mfa/webauthn/login-options');
      const assertionResponse = await startAuthentication({
        optionsJSON: data.options,
      });
      const { data: verifyData } = await customFetch.post('/auth/mfa/verify', {
        method: 'webauthn',
        assertionResponse,
      });
      finishLogin(verifyData.tokenUser);
    } catch (error) {
      // Abbruch durch den User nicht als Fehler anzeigen
      if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
        handleVerifyError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectMethod = (newMethod) => {
    setMethod(newMethod);
    setCode('');
  };

  const tabMethods = [...methods, ...(hasRecoveryCodes ? ['recovery'] : [])];
  const showCodeInput = ['totp', 'emailOtp', 'recovery'].includes(method);

  return (
    <div className="h-full bg-gray-50" style={{ height: '100vh' }}>
      <div className="h-full">
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <Link to="/">
              <LogoLang />
            </Link>
            <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
              Bestätige deine Anmeldung
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Dein Account ist mit Bestätigung in zwei Schritten geschützt.
            </p>
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
            <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
              {tabMethods.length > 1 && (
                <div className="mb-6 flex flex-wrap gap-2">
                  {tabMethods.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => selectMethod(m)}
                      className={
                        method === m
                          ? 'rounded-full bg-svisit-primary-600 px-3 py-1 text-xs font-semibold text-white'
                          : 'rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200'
                      }
                    >
                      {methodLabels[m] || 'Recovery-Code'}
                    </button>
                  ))}
                </div>
              )}

              {method === 'totp' && (
                <p className="mb-4 text-sm text-gray-700">
                  Gib den 6-stelligen Code aus deiner Authenticator-App ein.
                </p>
              )}

              {method === 'emailOtp' && (
                <div className="mb-4 space-y-4">
                  <p className="text-sm text-gray-700">
                    Wir senden dir einen 6-stelligen Code an{' '}
                    <span className="font-semibold">{email}</span>.
                  </p>
                  <button
                    type="button"
                    onClick={sendEmailCode}
                    disabled={isSubmitting || emailCooldown > 0}
                    className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    {emailCooldown > 0
                      ? `Code erneut senden (${emailCooldown}s)`
                      : emailCodeSent
                      ? 'Code erneut senden'
                      : 'Code senden'}
                  </button>
                </div>
              )}

              {method === 'recovery' && (
                <p className="mb-4 text-sm text-gray-700">
                  Gib einen deiner Recovery-Codes ein (Format: XXXX-XXXX). Jeder
                  Code funktioniert nur einmal.
                </p>
              )}

              {showCodeInput && (
                <form className="space-y-6" onSubmit={submitCode}>
                  <div>
                    <label
                      htmlFor="code"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      {method === 'recovery' ? 'Recovery-Code' : 'Code'}
                    </label>
                    <div className="mt-2">
                      <input
                        id="code"
                        name="code"
                        type="text"
                        inputMode={method === 'recovery' ? 'text' : 'numeric'}
                        autoComplete="one-time-code"
                        autoFocus
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={
                          method === 'recovery' ? 'XXXX-XXXX' : '123456'
                        }
                        className={inputClasses}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className={primaryButtonClasses}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Prüft...' : 'Bestätigen'}
                  </button>
                </form>
              )}

              {method === 'webauthn' && (
                <button
                  type="button"
                  onClick={verifyWithPasskey}
                  className={primaryButtonClasses}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Wartet auf Passkey...' : 'Mit Passkey bestätigen'}
                </button>
              )}

              <div className="mt-6 text-center text-sm leading-6">
                <Link
                  to="/login"
                  className="font-semibold text-svisit-primary-600 hover:text-svisit-primary-500"
                >
                  Zurück zum Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaVerify;
